// Copyright © SRGTech (Software Resonance Group Inc.). All rights reserved.
// SPDX-License-Identifier: LicenseRef-SRGTech-Proprietary
// See LICENSE and docs/legal/PROPRIETARY_COPYRIGHT.md.

/**
 * The single browser transport used by every shared API client.
 *
 * Security properties enforced here, once, so no feature can opt out:
 *
 * - the request carries the BFF session cookie (`credentials: 'include'`) and
 *   the UI never reads, stores, or forwards a bearer token
 *   (`SEC2-019`, `07-SECURITY.md` §10.3 and §15.3);
 * - cookie-authenticated unsafe methods carry the double-submit CSRF header
 *   (`07-SECURITY.md` §10.3);
 * - requests are same-origin relative to a configured BFF base path, so a
 *   caller cannot direct the browser at an arbitrary destination
 *   (`SEC2-020` outbound destination control);
 * - every request has an absolute deadline and propagates caller cancellation
 *   (`RUN-001`, `FAP-024`);
 * - responses are size bounded before parsing (`API-012` unbounded results);
 * - responses are validated with Zod before entering application code
 *   (`docs/05-CODING-STANDARDS.md` §9); and
 * - failures always surface as {@link ApiProblemError}, never as a raw
 *   exception or a partially parsed body.
 */

import type { ZodType } from 'zod';

import { ApiProblemError, PROBLEM_JSON_MEDIA_TYPE, problemFromResponse } from './problem';

/** Default per-request deadline in milliseconds. */
export const DEFAULT_TIMEOUT_MS = 15_000;

/** Maximum accepted response size in bytes. */
export const MAX_RESPONSE_BYTES = 2 * 1024 * 1024;

/** Header carrying the double-submit CSRF token. */
export const CSRF_HEADER = 'x-eai-csrf-token';

/** Name of the non-`HttpOnly` cookie holding the double-submit CSRF token. */
export const CSRF_COOKIE = 'eai_csrf';

/** HTTP methods that require CSRF protection. */
const UNSAFE_METHODS: ReadonlySet<string> = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

/** Supported request methods. */
export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

/**
 * The single operation permitted to state that CSRF is not required.
 *
 * Sign-in is the one unsafe request that necessarily precedes the double-submit
 * cookie: the BFF mints `eai_csrf` only on a successful login
 * (`routes/auth/router.py::_set_session_cookies`), so a transport that demanded
 * the token here would refuse the very request that creates it. The BFF's login
 * route correspondingly does not call `validate_csrf`; it defends itself with
 * exact-`Origin` validation and login rate limiting instead.
 *
 * The exemption is named rather than boolean on purpose. There is no
 * `skipCsrf: true`: a caller must name *this* operation, on *this* path, with
 * *this* method, and any deviation is rejected before the request is built.
 */
export const BOOTSTRAP_LOGIN_OPERATION = 'bootstrapLogin';

/** The one path the CSRF exemption may be used on, after normalisation. */
export const BOOTSTRAP_LOGIN_PATH = '/auth/bootstrap/login';

/** The one method the CSRF exemption may be used with. */
export const BOOTSTRAP_LOGIN_METHOD = 'POST';

/**
 * Per-request CSRF disposition.
 *
 * Omitted means `required`, so every existing and future unsafe call keeps the
 * default protection without stating anything.
 */
export type CsrfRequirement = 'required' | 'not-required';

/** Operations that may name themselves to the transport. */
export type NamedOperation = typeof BOOTSTRAP_LOGIN_OPERATION;

/** Query parameters; `undefined` values are omitted. */
export type QueryParams = Readonly<Record<string, string | number | boolean | undefined>>;

/** Options for a single request. */
export interface RequestOptions<TResponse> {
  /** Path relative to the configured base path, e.g. `/control-plane/tenants`. */
  readonly path: string;
  /** HTTP method; defaults to `GET`. */
  readonly method?: HttpMethod;
  /** Query parameters appended to the path. */
  readonly query?: QueryParams;
  /** JSON request body for unsafe methods. */
  readonly body?: unknown;
  /** Schema that validates the successful response body. */
  readonly schema: ZodType<TResponse>;
  /** Caller cancellation signal, combined with the client deadline. */
  readonly signal?: AbortSignal | undefined;
  /** Per-request deadline override in milliseconds. */
  readonly timeoutMs?: number;
  /**
   * Whether this request carries the double-submit CSRF token.
   *
   * Defaults to `required`. `not-required` is honoured only for the bootstrap
   * sign-in — see {@link BOOTSTRAP_LOGIN_OPERATION} — and is rejected outright
   * anywhere else.
   */
  readonly csrf?: CsrfRequirement;
  /**
   * The operation this request performs, for the callers that must name
   * themselves. Only {@link BOOTSTRAP_LOGIN_OPERATION} is defined, and only
   * because it is the sole holder of the CSRF exemption.
   */
  readonly operation?: NamedOperation;
}

/** Configuration for {@link createHttpClient}. */
export interface HttpClientConfig {
  /**
   * Same-origin base path for the BFF, for example `/api/v1`.
   *
   * An absolute URL is rejected: the browser must never be pointed at an
   * arbitrary origin by configuration (`SEC2-020`).
   */
  readonly basePath: string;
  /** Injected `fetch`; defaults to the global implementation. */
  readonly fetchImpl?: typeof fetch;
  /** Reads the current CSRF token; defaults to the double-submit cookie. */
  readonly readCsrfToken?: () => string | undefined;
  /** Default deadline in milliseconds. */
  readonly timeoutMs?: number;
}

/** The transport exposed to typed resource clients. */
export interface HttpClient {
  request<TResponse>(options: RequestOptions<TResponse>): Promise<TResponse>;
}

/**
 * Read the double-submit CSRF token from `document.cookie`.
 *
 * The token cookie is intentionally readable by scripts; the session cookie is
 * `HttpOnly` and is not (`07-SECURITY.md` §10.3).
 *
 * @returns The token, or `undefined` when absent.
 */
export function readCsrfCookie(): string | undefined {
  if (typeof document === 'undefined') {
    return undefined;
  }
  for (const entry of document.cookie.split(';')) {
    const [rawName, ...rest] = entry.split('=');
    if (rawName?.trim() === CSRF_COOKIE) {
      const value = rest.join('=').trim();
      return value === '' ? undefined : decodeURIComponent(value);
    }
  }
  return undefined;
}

function assertSameOriginBasePath(basePath: string): void {
  if (!basePath.startsWith('/') || basePath.startsWith('//')) {
    throw new RangeError(
      'HttpClient basePath must be a same-origin absolute path such as "/api/v1".',
    );
  }
}

/**
 * Reduce a request path to the form the CSRF exemption is matched against.
 *
 * Percent-encoding, a trailing slash, dot segments, and repeated separators are
 * all ways to spell a path that is not literally the login route, so they are
 * resolved first and the result is compared exactly. Anything that cannot be
 * decoded normalises to a value that matches nothing, which fails closed.
 *
 * @param path - The request path as supplied by the caller.
 * @returns The normalised path, or the empty string when it cannot be read.
 */
function normalisePath(path: string): string {
  let decoded: string;
  try {
    decoded = decodeURIComponent(path);
  } catch {
    return '';
  }
  const segments: string[] = [];
  for (const segment of decoded.split('/')) {
    if (segment === '' || segment === '.') {
      continue;
    }
    if (segment === '..') {
      segments.pop();
      continue;
    }
    segments.push(segment);
  }
  return `/${segments.join('/')}`;
}

/**
 * Decide whether a request is exactly the bootstrap sign-in.
 *
 * All three conditions must hold: the method is `POST`, the normalised path is
 * exactly the login route, and the caller named itself as the bootstrap sign-in
 * operation. Two out of three is not the login request.
 *
 * @param method - The resolved HTTP method.
 * @param path - The request path as supplied by the caller.
 * @param operation - The operation the caller named, if any.
 * @returns Whether the CSRF exemption applies.
 */
function isBootstrapLogin(
  method: string,
  path: string,
  operation: NamedOperation | undefined,
): boolean {
  return (
    method === BOOTSTRAP_LOGIN_METHOD &&
    normalisePath(path) === BOOTSTRAP_LOGIN_PATH &&
    operation === BOOTSTRAP_LOGIN_OPERATION
  );
}

function buildUrl(basePath: string, path: string, query: QueryParams | undefined): string {
  if (!path.startsWith('/')) {
    throw new RangeError(`Request path must start with "/": ${path}`);
  }
  const normalisedBase = basePath.endsWith('/') ? basePath.slice(0, -1) : basePath;
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value !== undefined) {
      search.append(key, String(value));
    }
  }
  const suffix = search.size > 0 ? `?${search.toString()}` : '';
  return `${normalisedBase}${path}${suffix}`;
}

async function readBoundedText(response: Response): Promise<string> {
  const declared = response.headers.get('content-length');
  if (declared !== null && Number(declared) > MAX_RESPONSE_BYTES) {
    throw new ApiProblemError('The response exceeded the accepted size limit.', {
      kind: 'server',
      status: response.status,
    });
  }
  const text = await response.text();
  if (text.length > MAX_RESPONSE_BYTES) {
    throw new ApiProblemError('The response exceeded the accepted size limit.', {
      kind: 'server',
      status: response.status,
    });
  }
  return text;
}

function parseJson(text: string, status: number): unknown {
  if (text === '') {
    return undefined;
  }
  try {
    return JSON.parse(text) as unknown;
  } catch (cause) {
    throw new ApiProblemError('The server returned a response that could not be read.', {
      kind: 'server',
      status,
      cause,
    });
  }
}

function transportError(cause: unknown, deadlineReached: boolean): ApiProblemError {
  if (deadlineReached) {
    return new ApiProblemError('The request took too long and was cancelled.', {
      kind: 'timeout',
      status: 0,
      cause,
    });
  }
  return new ApiProblemError('The service could not be reached.', {
    kind: 'offline',
    status: 0,
    cause,
  });
}

/**
 * Create the shared HTTP client.
 *
 * @param config - Transport configuration.
 * @returns A client that returns validated typed responses.
 * @throws RangeError If `basePath` is not a same-origin absolute path.
 */
export function createHttpClient(config: HttpClientConfig): HttpClient {
  assertSameOriginBasePath(config.basePath);
  const fetchImpl = config.fetchImpl ?? globalThis.fetch.bind(globalThis);
  const readToken = config.readCsrfToken ?? readCsrfCookie;
  const defaultTimeout = config.timeoutMs ?? DEFAULT_TIMEOUT_MS;

  return {
    async request<TResponse>(options: RequestOptions<TResponse>): Promise<TResponse> {
      const method = options.method ?? 'GET';

      // Checked before anything is allocated or sent. A caller that claims the
      // exemption without being the bootstrap sign-in never reaches `fetch`,
      // whether or not a token happens to be available — the misuse is refused
      // rather than quietly downgraded to a protected request.
      const csrfExempt = options.csrf === 'not-required';
      if (csrfExempt && !isBootstrapLogin(method, options.path, options.operation)) {
        throw new RangeError(
          `CSRF is required for ${method} ${options.path}; the "not-required" disposition is ` +
            `reserved for ${BOOTSTRAP_LOGIN_METHOD} ${BOOTSTRAP_LOGIN_PATH} as the ` +
            `"${BOOTSTRAP_LOGIN_OPERATION}" operation.`,
        );
      }

      const url = buildUrl(config.basePath, options.path, options.query);
      const controller = new AbortController();
      const timeoutMs = options.timeoutMs ?? defaultTimeout;
      let deadlineReached = false;

      const timer = setTimeout(() => {
        deadlineReached = true;
        controller.abort();
      }, timeoutMs);
      const abortFromCaller = (): void => {
        controller.abort();
      };
      options.signal?.addEventListener('abort', abortFromCaller);

      const headers: Record<string, string> = { accept: 'application/json' };
      if (UNSAFE_METHODS.has(method) && !csrfExempt) {
        const token = readToken();
        if (token === undefined) {
          clearTimeout(timer);
          options.signal?.removeEventListener('abort', abortFromCaller);
          throw new ApiProblemError(
            'The session could not be verified. Reload the page and try again.',
            { kind: 'unauthenticated', status: 0 },
          );
        }
        headers[CSRF_HEADER] = token;
      }
      if (options.body !== undefined) {
        headers['content-type'] = 'application/json';
      }

      let response: Response;
      try {
        response = await fetchImpl(url, {
          method,
          headers,
          credentials: 'include',
          redirect: 'error',
          cache: 'no-store',
          referrerPolicy: 'same-origin',
          signal: controller.signal,
          ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
        });
      } catch (cause) {
        throw transportError(cause, deadlineReached);
      } finally {
        clearTimeout(timer);
        options.signal?.removeEventListener('abort', abortFromCaller);
      }

      const text = await readBoundedText(response);

      if (!response.ok) {
        const contentType = response.headers.get('content-type') ?? '';
        const body = contentType.includes(PROBLEM_JSON_MEDIA_TYPE)
          ? parseJson(text, response.status)
          : undefined;
        throw problemFromResponse(response, body);
      }

      const parsed = options.schema.safeParse(parseJson(text, response.status));
      if (!parsed.success) {
        throw new ApiProblemError('The server returned data in an unexpected format.', {
          kind: 'server',
          status: response.status,
          cause: parsed.error,
        });
      }
      return parsed.data;
    },
  };
}
